/* eslint-disable no-nested-ternary */
/* eslint-disable import/prefer-default-export */
/* eslint-disable no-underscore-dangle */

import {
  NormalizedBase,
  NormalizedDrone,
  RawApiResponse,
  RawBase,
  RawBaseWsData,
  RawDrone,
  RawDroneWsMessage,
  SortKey,
  SystemStatus,
  OPERATIONAL_BASE_STATUSES,
  MAINTENANCE_BASE_STATUSES,
  OPERATIONAL_DRONE_STATUSES,
  MAINTENANCE_DRONE_STATUSES,
  GROUNDED_DRONE_STATUSES,
  PLANNED_BASE_STATUSES,
  NormalizedApiResponse,
  RawAddress,
  RawMissionManagerUrl,
  NormalizedMissionManagerUrl,
} from '../types/dashboard';
import { WS_BASE_STATUS_NORMALIZED_MAP } from './dashboard-constants';

/* ---------------------- Display Formatters ---------------------- */

/**
 * Format an ISO date string into a human-readable format
 */
export function formatLastFlown(isoString: string | null): string | null {
  if (!isoString) return null;
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a duration in minutes into a human-readable format.
 *
 */
export function formatMissionDuration(minutes: number | null): string | null {
  if (minutes == null) return null;
  const roundedMinutes = Math.round(minutes);
  const days = Math.floor(roundedMinutes / 1440);
  const hours = Math.floor((roundedMinutes % 1440) / 60);
  const mins = roundedMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
  if (mins > 0) parts.push(`${mins} min`);

  return parts.length > 0 ? parts.join(' ') : '0 min';
}

/**
 * Convert a duration in milliseconds to minutes (rounded to 1 decimal place)
 */
export function millisecondsToMinutes(
  milliseconds: number | null
): number | null {
  if (milliseconds === null) return null;
  return Math.round((milliseconds / 60000) * 10) / 10;
}

/**
 * Format the drone model string from the API into a user-readable format
 */
export function formatDroneModel(model: string): string {
  if (model === 'blockFive' || model === 'blockFiveT') return 'Block 5';
  return 'Block 4';
}

/* ---------------------- Status Helpers ---------------------- */

/*
 * Helper function to determine if a base or drone is retired based on its status string.
 */
export function isRetired(status: string | null): boolean {
  return status?.toLowerCase() === 'retired';
}

/*
 * Normalizes raw WebSocket base status strings to match the canonical API status strings.
 * If the WS status is unrecognized, returns null so it doesn't affect SystemStatus derivation.
 */
export function normalizeWsBaseStatus(wsStatus: string): string | null {
  return WS_BASE_STATUS_NORMALIZED_MAP[wsStatus.toLowerCase()] ?? null;
}

/**
 * Derive the SystemStatus for a base given its own string status
 * and its list of normalized drones.
 *
 * Priority: Operational -> Maintenance -> Grounded -> Offline
 *
 * - OPERATIONAL:  base is operational AND at least one drone is operational
 *                 (and not grounded)
 * - MAINTENANCE:  base is in maintenance OR any drone is in maintenance
 * - GROUNDED:     any drone is grounded (isGrounded flag or grounded status string)
 * - OFFLINE:      everything not covered in one of the above states, OR when the
 *                 property stopReportToFmForMoreThan30Mins is true for the base or
 *                 not true for any of its drones
 */
export function deriveSystemStatus(
  baseStatus: string | null,
  drones: NormalizedDrone[],
  stopReportToFmForMoreThan30MinsForBase?: boolean | null
): SystemStatus {
  if (!baseStatus) {
    return SystemStatus.OFFLINE;
  }
  const baseIsOperational = OPERATIONAL_BASE_STATUSES.has(baseStatus);
  const baseIsMaintenance = MAINTENANCE_BASE_STATUSES.has(baseStatus);
  const baseIsPlanned = PLANNED_BASE_STATUSES.has(baseStatus);

  // If the base status is "Planned", return PLANNED regardless of drone statuses, since the base isn't active yet
  if (baseIsPlanned) return SystemStatus.PLANNED;

  // If the base has stopped reporting for more than 30 minutes, return the OFFLINE state. If none of the drones have reported in the last 30 minutes, return OFFLINE
  const baseOfflineByTimeout = stopReportToFmForMoreThan30MinsForBase === true;
  const atLeastOneDroneHasHeartbeat = drones.some(
    (drone) => drone?.isOfflineDueToReportingTimeout === false
  );
  if (
    baseOfflineByTimeout ||
    (drones.length > 0 && !atLeastOneDroneHasHeartbeat)
  ) {
    return SystemStatus.OFFLINE;
  }

  const hasOperationalDrone = drones.some(
    (drone) =>
      !drone?.isGrounded &&
      drone?.status != null &&
      OPERATIONAL_DRONE_STATUSES.has(drone?.status)
  );
  const hasMaintenanceDrone = drones.some(
    (drone) =>
      drone?.status != null && MAINTENANCE_DRONE_STATUSES.has(drone?.status)
  );
  const hasGroundedDrone = drones.some(
    (drone) =>
      drone?.isGrounded ||
      (drone?.status != null && GROUNDED_DRONE_STATUSES.has(drone?.status))
  );

  if (baseIsOperational && hasOperationalDrone) return SystemStatus.OPERATIONAL;
  if (baseIsMaintenance || hasMaintenanceDrone) return SystemStatus.MAINTENANCE;
  if (hasGroundedDrone) return SystemStatus.GROUNDED;
  return SystemStatus.OFFLINE;
}

/* ---------------------- Address Helpers ---------------------- */

// Capitalize the first letter of a string and make the rest lowercase
function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Builds a full address string from the API address fields.
 * Format: "Street, Suite, City, State, Zip, Country"
 * Empty/null fields are filtered out automatically.
 */
export function buildFullAddress(address?: RawAddress | null): string {
  return [
    address?.addressLineOne ? toTitleCase(address?.addressLineOne) : null,
    address?.addressLineTwo ? toTitleCase(address?.addressLineTwo) : null,
    address?.cityOrTown ? toTitleCase(address?.cityOrTown) : null,
    address?.governingDistrict
      ? address?.governingDistrict.toUpperCase()
      : null,
    address?.zipCode,
    address?.country ? address?.country.toUpperCase() : null,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * Builds a short address string.
 * - Normally: "City, State"
 * - If another base shares the same city & state, use: "City, State (Street)"
 */
export function buildPartialAddress(
  address: RawAddress | null,
  isDuplicateCity: boolean
): string {
  const cityState = [
    address?.cityOrTown ? toTitleCase(address?.cityOrTown) : null,
    address?.governingDistrict
      ? address?.governingDistrict.toUpperCase()
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  if (isDuplicateCity && address?.addressLineOne) {
    return `${cityState} (${toTitleCase(address?.addressLineOne)})`;
  }
  return cityState;
}

/**
 * Builds a Set of "City|State" keys where more than one base
 * shares the same city and state. Used to decide whether a base's
 * partial address should include the street name.
 */
function buildDuplicateCitySet(rawBases: RawBase[]): Set<string> {
  const counts = rawBases.reduce<Record<string, number>>((acc, base) => {
    const address = base?.currentLocation?.address ?? null;
    const city = address?.cityOrTown ?? null;
    const district = address?.governingDistrict ?? null;

    if (!city || !district) {
      return acc;
    }

    const key = `${city}|${district}`;
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  return new Set(
    Object.entries(counts)
      .filter(([, count]) => count > 1)
      .map(([key]) => key)
  );
}

/* ---------------------- Mission Manager URL Normalization ---------------------- */

/**
 * Normalizes a single RawMissionManagerUrl from the API into a NormalizedMissionManagerUrl.
 * If the input doesn't have a domain, returns null to filter it out.
 * Builds a full https:// URL from the domain the API returns.
 */
export function normalizeMissionManagerUrl(
  raw: RawMissionManagerUrl
): NormalizedMissionManagerUrl | null {
  if (!raw || !raw.domain) return null;
  const trimmedDomain = raw.domain.trim();
  if (!trimmedDomain) return null;

  const url = /^https?:\/\//i.test(trimmedDomain)
    ? trimmedDomain
    : `https://${trimmedDomain}`;

  return {
    id: raw?._id ?? null,
    name: raw?.name ?? null,
    url,
  };
}

/* ---------------------- Drone Normalization ---------------------- */

/**
 * Normalizes a single RawDrone from the API into a NormalizedDrone.
 */
export function normalizeDrone(raw: RawDrone): NormalizedDrone | null {
  if (!raw || !raw._id || !raw.name) return null;
  return {
    id: raw._id,
    name: raw?.name,
    faaRegistrationNumber: raw?.faaRegistrationNumber ?? null,
    remoteIDnumber: raw?.remoteIDnumber ?? null,

    model: raw?.model ?? null,

    status: raw?.status ?? null,
    isGrounded: raw?.status != null && GROUNDED_DRONE_STATUSES.has(raw?.status),
    isOfflineDueToReportingTimeout:
      raw?.stopReportToFmForMoreThan30Mins === true,

    batteryLevel: null, // populated by WS
    chargingCycles: null, // populated by WS
    charging: null, // populated by WS

    totalFlightMinutes:
      raw?.totalFlightMinutes != null ? raw?.totalFlightMinutes : null,
    firstActivated: raw?.firstActivated ?? null,
  };
}

/**
 * Merges a drone WS message into an existing NormalizedDrone.
 * WS data always wins over API data for overlapping fields.
 * Returns a new object — does not mutate.
 */
export function applyDroneWsUpdate(
  drone: NormalizedDrone,
  ws: RawDroneWsMessage
): NormalizedDrone {
  return {
    ...drone,
    isGrounded: ws?.grounded ?? drone?.isGrounded,

    batteryLevel:
      ws?.battery != null
        ? parseFloat(ws.battery.toFixed(1))
        : ws?.battery_charge_state != null
        ? parseFloat((ws.battery_charge_state * 100).toFixed(1))
        : drone?.batteryLevel,
    chargingCycles:
      ws?.bms_cycle_count != null ? ws.bms_cycle_count : drone?.chargingCycles,
    charging: ws?.bms_current != null ? ws.bms_current > 0 : drone?.charging,
  };
}

/* ---------------------- Base Normalization ---------------------- */

/**
 * Normalizes a single RawBase from the API into a NormalizedBase.
 * Requires the duplicate-city Set so partial addresses can be disambiguated.
 * Retired drones are filtered out before any other processing.
 */
export function normalizeBase(
  raw: RawBase,
  duplicateCities: Set<string>
): NormalizedBase | null {
  if (!raw || !raw._id || !raw.name) {
    return null;
  }
  const addr = raw?.currentLocation?.address ?? null;
  const cityKey = `${addr?.cityOrTown ?? ''}|${addr?.governingDistrict ?? ''}`;

  // filter out retired drones, then normalize the rest
  const drones = (raw?.drones ?? [])
    .filter((d) => !isRetired(d?.status ?? null))
    .map(normalizeDrone)
    .filter((d): d is NormalizedDrone => d !== null);

  // normalize mission manager URLs, filtering out any that are null. If none remain, set to null
  const normalizedMissionManagerUrls = (
    raw?.currentLocation?.missionManagerUrls ?? []
  )
    .map(normalizeMissionManagerUrl)
    .filter((mmUrl): mmUrl is NormalizedMissionManagerUrl => mmUrl !== null);
  const missionManagerUrls =
    normalizedMissionManagerUrls.length > 0
      ? normalizedMissionManagerUrls
      : null;

  const baseStatus = raw?.status ?? null;
  const isOfflineDueToReportingTimeout =
    raw?.stopReportToFmForMoreThan30Mins === true;
  const systemStatus = deriveSystemStatus(
    baseStatus,
    drones,
    isOfflineDueToReportingTimeout
  );

  const successRate =
    raw?.totalMissions != null && raw?.successfulMissions != null
      ? raw?.totalMissions === 0
        ? 0
        : Math.round((raw.successfulMissions / raw.totalMissions) * 100)
      : null;

  return {
    id: raw._id,
    name: raw?.name,

    fullAddress: buildFullAddress(addr),
    partialAddress: buildPartialAddress(addr, duplicateCities.has(cityKey)),

    lat:
      raw?.locationOverride?.lat ??
      raw?.location_override?.lat ??
      raw?.lat ??
      null,
    lon:
      raw?.locationOverride?.lon ??
      raw?.location_override?.lon ??
      raw?.lon ??
      null,

    systemStatus,
    baseStatus,
    chargingStatus: null, // populated by WS
    isOfflineDueToReportingTimeout,

    totalMissions: raw?.totalMissions ?? 0,
    successRate,
    numberOfNightMissions: raw?.totalNightMissions ?? 0,
    numberOfDayMissions: Math.max(
      (raw?.totalMissions ?? 0) - (raw?.totalNightMissions ?? 0),
      0
    ),

    averageMissionDuration: millisecondsToMinutes(
      raw?.averageMissionDuration ?? null
    ),
    longestMissionDuration: millisecondsToMinutes(
      raw?.longestMissionDuration ?? null
    ),
    shortestMissionDuration: millisecondsToMinutes(
      raw?.shortestMissionDuration ?? null
    ),
    totalFlightMinutesBase: millisecondsToMinutes(
      raw?.totalFlightTimeBase ?? null
    ),

    lastMissionLandingDate: raw?.lastMissionLandingDate ?? null,
    totalNumberOfRaidEvents: raw?.totalNumberOfRaidEvents ?? null,
    weeklyScheduledMissions: raw?.weeklyScheduledMissions ?? null,

    missionManagerUrls,
    drones,
  };
}

/**
 * Normalizes the full API response into a NormalizedBase array.
 * Builds the duplicate-city Set once up front so all bases share it.
 * Removes retired bases here, and retired drones inside normalizeBase.
 */
export function normalizeApiResponse(
  response: RawApiResponse
): NormalizedApiResponse {
  const usableBases = (response.bases ?? []).filter(
    (base) => base._id && base.name && !isRetired(base.status ?? null)
  );
  const duplicateCities = buildDuplicateCitySet(usableBases);
  return {
    bases: usableBases
      .map((raw) => normalizeBase(raw, duplicateCities))
      .filter((base): base is NormalizedBase => base !== null),
    startDate: response?.startDate ?? null,
    endDate: response?.endDate ?? null,
  };
}

/* ---------------------- WebSocket Merge Functions ---------------------- */

/**
 * Applies a drone WS update to the correct drone inside the correct base.
 * Matches on drone name. After updating the drone, re-derives the base's
 * SystemStatus since grounded state may have changed.
 * Returns a new bases array — does not mutate.
 */
export function applyDroneWsUpdateToBases(
  bases: NormalizedBase[],
  ws: RawDroneWsMessage
): NormalizedBase[] {
  if (!ws.base_name || !ws.name) return bases;

  let changed = false;

  const next = bases.map((base) => {
    if (base?.name !== ws?.base_name) return base;

    const drones = base.drones ?? [];
    const droneIndex = drones.findIndex((d) => d?.name === ws?.name);
    if (droneIndex === -1) return base;

    const updatedDrones = drones.map((d, i) =>
      i === droneIndex ? applyDroneWsUpdate(d, ws) : d
    );

    const hasChargingData = updatedDrones.some((d) => d.charging != null);
    const isCharging = updatedDrones.some((d) => d.charging === true);

    changed = true;
    return {
      ...base,
      drones: updatedDrones,
      systemStatus: deriveSystemStatus(
        base?.baseStatus,
        updatedDrones,
        base?.isOfflineDueToReportingTimeout
      ),
      chargingStatus: hasChargingData
        ? isCharging
          ? 'Charging'
          : 'Standby'
        : null,
    };
  });

  // If nothing changed, return the original array reference so React
  // skips the re-render entirely
  return changed ? next : bases;
}

/**
 * Applies a base WS update to the correct base.
 * Normalizes the kebab-case WS status to match the API format,
 * updates baseStatus, then re-derives SystemStatus.
 * Returns a new bases array — does not mutate.
 */
export function applyBaseWsUpdateToBases(
  bases: NormalizedBase[],
  ws: RawBaseWsData
): NormalizedBase[] {
  if (!ws?.name) {
    return bases;
  }

  let changed = false;

  const next = bases.map((base) => {
    if (base?.name !== ws?.name) return base;

    // Get the status from the WS message, and normalize it to match the API status strings. If it doesn't match any known status, ignore it and use the base's current status
    const normalizedStatus =
      ws?.status !== null && ws?.status !== undefined
        ? normalizeWsBaseStatus(ws?.status)
        : null;
    const hasStatusUpdate = normalizedStatus != null;

    // Update the location if provided by the WS message
    const wsLat = ws?.location_override?.lat ?? null;
    const wsLon = ws?.location_override?.lon ?? null;
    const hasLocationUpdate = wsLat != null && wsLon != null;

    // If neither status nor location is updated, return the original base object to avoid unnecessary re-renders
    if (!hasStatusUpdate && !hasLocationUpdate) {
      return base;
    }

    // Find the new base status (use the normalized WS status if available, otherwise keep the existing status)
    const newBaseStatus = hasStatusUpdate ? normalizedStatus : base?.baseStatus;

    changed = true;
    return {
      ...base,
      baseStatus: newBaseStatus,
      lat: hasLocationUpdate ? wsLat : base?.lat,
      lon: hasLocationUpdate ? wsLon : base?.lon,
      systemStatus: deriveSystemStatus(
        newBaseStatus,
        base?.drones ?? [],
        base?.isOfflineDueToReportingTimeout
      ),
    };
  });

  return changed ? next : bases;
}

/*
 * Merges incoming API response data into a list of existing bases, preserving websocket-updated fields where possible
 */
export function mergeApiResponseIntoBases(
  incomingBases: NormalizedBase[],
  existingBases: NormalizedBase[]
): NormalizedBase[] {
  const existingBasesMap = new Map(
    existingBases
      .filter((base) => base.name != null)
      .map((base) => [base.name, base])
  );

  return incomingBases.map((incomingBase) => {
    const existingBase =
      incomingBase?.name != null
        ? existingBasesMap.get(incomingBase.name)
        : null;
    if (!existingBase) return incomingBase;

    const existingDronesMap = new Map(
      (existingBase?.drones ?? [])
        .filter((drone) => drone.name != null)
        .map((drone) => [drone.name, drone])
    );

    const mergedDrones = (incomingBase?.drones ?? []).map((incomingDrone) => {
      const existingDrone =
        incomingDrone?.name != null
          ? existingDronesMap.get(incomingDrone.name)
          : null;
      if (!existingDrone) return incomingDrone;
      return {
        ...incomingDrone,
        batteryLevel: existingDrone?.batteryLevel,
        chargingCycles: existingDrone?.chargingCycles,
        charging: existingDrone?.charging,
        isGrounded: existingDrone?.isGrounded,
      };
    });

    return {
      ...incomingBase,
      drones: mergedDrones,
      chargingStatus: existingBase?.chargingStatus,
      systemStatus: deriveSystemStatus(
        incomingBase?.baseStatus,
        mergedDrones,
        incomingBase?.isOfflineDueToReportingTimeout
      ),
    };
  });
}

/* ---------------------- Sorting ---------------------- */

/**
 * Comparator functions for each SortKey.
 * Return 0 on a tie — the caller falls back to base name.
 */
export const BASE_SORTERS: Record<
  SortKey,
  (a: NormalizedBase, b: NormalizedBase) => number
> = {
  [SortKey.MOST_MISSIONS]: (a, b) =>
    (b?.totalMissions ?? 0) - (a?.totalMissions ?? 0),
  [SortKey.AVERAGE_MISSION_DURATION]: (a, b) =>
    (b?.averageMissionDuration ?? 0) - (a?.averageMissionDuration ?? 0),
  [SortKey.NUMBER_OF_NIGHT_MISSIONS]: (a, b) =>
    (b?.numberOfNightMissions ?? 0) - (a?.numberOfNightMissions ?? 0),
  [SortKey.NUMBER_OF_DAY_MISSIONS]: (a, b) =>
    (b?.numberOfDayMissions ?? 0) - (a?.numberOfDayMissions ?? 0),
  [SortKey.BASE_NAME]: (a, b) =>
    (a?.name ?? '').localeCompare(b?.name ?? '', undefined, {
      sensitivity: 'base',
    }),
  [SortKey.LOCATION_NAME]: (a, b) => {
    const aLoc = a?.partialAddress;
    const bLoc = b?.partialAddress;

    const aUnknown = !aLoc;
    const bUnknown = !bLoc;

    if (aUnknown && bUnknown) return 0;
    if (aUnknown) return 1;
    if (bUnknown) return -1;

    return aLoc.localeCompare(bLoc, undefined, { sensitivity: 'base' });
  },
};

/**
 * Returns a new sorted array of NormalizedBase objects.
 * Ties are broken alphabetically by base name.
 */
export function sortBases(
  bases: NormalizedBase[],
  sortKey: SortKey
): NormalizedBase[] {
  return [...bases].sort((a, b) => {
    const primary = BASE_SORTERS[sortKey](a, b);
    if (primary !== 0) return primary;
    return (a?.name ?? '').localeCompare(b?.name ?? '');
  });
}

/* ---------------------- Misc Helpers ---------------------- */

/**
 * Returns the highest battery level among all drones on a base that have
 * a battery value. Returns null if no drones have battery data yet.
 */
export function getHighestBatteryLevel(
  drones: NormalizedDrone[] | null
): number | null {
  if (!drones?.length) return null;
  const levels = drones
    .map((d) => d.batteryLevel)
    .filter((b): b is number => b != null);
  if (!levels.length) return null;
  return Math.max(...levels);
}
